import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import isValid from 'date-fns/isValid';

import useStore, { type StoreState } from 'services/store';
import { parseQueryString, updateQueryStringMultiple } from 'utils/url';

import useCurrentPage from './useCurrentPage';

const isValidTimestamp = (timestamp: number) => {
  return !!timestamp && isValid(new Date(timestamp));
};

const selector = (state: StoreState) => ({
  lastSearch: state.searchQuery.lastSearch,
});

const hasValidTimerange = (search: string) => {
  const { from, to, q } = parseQueryString(search) || {};

  return !!q || (isValidTimestamp(+(from as string)) && isValidTimestamp(+(to as string)));
};

const useEnsureTimerange = (defaultQuery = 'Last 2 hours') => {
  const page = useCurrentPage();
  const location = useLocation();
  const navigate = useNavigate();
  const { lastSearch } = useStore(selector);

  const lastQueryString = lastSearch?.[page as string];

  useEffect(() => {
    if (hasValidTimerange(location.search)) {
      return;
    }

    if (lastQueryString && hasValidTimerange(lastQueryString)) {
      navigate(`${location.pathname}${lastQueryString}`, { replace: true });
      return;
    }

    navigate(updateQueryStringMultiple({ q: defaultQuery, from: undefined, to: undefined }), { replace: true });
  }, [navigate, defaultQuery, lastQueryString, location.pathname, location.search]);

  return null;
};

export default useEnsureTimerange;
